import { Reducer } from 'redux';
import { getServiceProviderType } from 'types';
import { AuthState, AuthActionTypes, UserRole } from './types';
import isTesting from '../../utilities/isTesting';
import { ConfigActionTypes } from '../config/types';
import { safeLocalStorageGet } from '../../utilities/localStorage';

const w = window as any;

const initialState: AuthState = {
  student_number: '',
  email: '',
  password: '',
  role: UserRole.Unknown,
  groups: [],
  terms: false,
  marketing: false,
  isAuthenticating: false,
  isCodeSent: false,
  uni: w?.globalConfig?.shortCode ?? 'STG',
  intake: '',
  institution: '',
  agencyId: '',
  pathwayAccess: 'None',
  currentUser: null,
  wsConnected: false,
  authAgencyData: null,
  authAgencyLoading: false,
  authAgencyError: null,
};

const reducer: Reducer<AuthState> = (state = initialState, action) => {
  // TODO -refactor this
  const getIntakeId = () => {
    const proposedIntake = state.intake || safeLocalStorageGet('dashboard-intake-filter-value') || '';

    const defaultIntake = action.payload.defaultIntakeId ?? '';

    const intakeList = action.payload.intakeIds?.map((intake: any) => intake.value);
    return intakeList?.includes(proposedIntake)
      ? proposedIntake
      : intakeList?.includes(defaultIntake)
        ? defaultIntake
        : intakeList
          ? intakeList[intakeList?.length - 1]
          : '';
  };

  switch (action.type) {
    case ConfigActionTypes.GET_UNI_CONFIG_SUCCESS:
      return { ...state, intake: getIntakeId() };
    case AuthActionTypes.COGNITO_REAUTH_REQUEST:
      if (isTesting) return state;
      return {
        ...state,
        isAuthenticating: true,
        validation_errors: undefined,
        role: UserRole.Unknown,
        server_errors: undefined,
      };
    case AuthActionTypes.COGNITO_REAUTH_FAILURE:
      if (isTesting) return state;
      return {
        ...state,
        isLoggedIn: false,
        role: UserRole.Unknown,
        isAuthenticating: false,
      };
    case AuthActionTypes.TEST_LOGIN_SUCCESS: {
      return {
        ...state,
        isTesting: true,
        isLoggedIn: true,
        isAuthenticating: false,
        student_number: '3',
        email: 'test@enroly.com',
        validation_errors: undefined,
        server_errors: undefined,
        role: action.payload.role,
        uni: action.payload.uni,
      };
    }
    case AuthActionTypes.COGNITO_LOGIN_SUCCESS: {
      const {
        groups,
        uni,
        pathway,
        terms: termsFromCognito,
        pathwayAccess,
        preferredUsername,
        username,
        agency,
        email,
        partner,
      } = action.payload.sessionContext;

      let role = UserRole.Unknown;

      const intake = safeLocalStorageGet('dashboard-intake-filter-value') || '';
      const institution = safeLocalStorageGet('dashboard-uni-institution-value') || '';

      let newUni = uni;
      let terms = true;
      if (groups && groups.includes('UniAdmin')) {
        role = UserRole.UniAdmin;
      } else if (groups && groups.includes('Admin')) {
        role = UserRole.Admin;
        newUni = (safeLocalStorageGet('dashboard-uni-filter-value') || uni) ?? 'STG';
      } else if (groups && groups.includes('UniSecure')) {
        role = UserRole.UniSecure;
      } else if (groups && groups.includes('Uni')) {
        role = UserRole.Uni;
      } else if (groups && groups.includes('UniLite')) {
        role = UserRole.UniLite;
      } else if (groups && groups.includes('Enroly')) {
        role = UserRole.Enroly;
        newUni = (safeLocalStorageGet('dashboard-uni-filter-value') || uni) ?? 'STG';
      } else if (groups && groups.includes('Agent')) {
        role = UserRole.Agent;
        newUni = (safeLocalStorageGet('dashboard-uni-filter-value') || uni) ?? 'AGT';
        terms = termsFromCognito === '1';
      } else if (groups && groups.includes('AgencyAdmin')) {
        role = UserRole.AgencyAdmin;
        newUni = (safeLocalStorageGet('dashboard-uni-filter-value') || uni) ?? 'AGT';
        terms = termsFromCognito === '1';
      } else if (groups && groups.includes('Partner')) {
        role = UserRole.Partner;
        newUni = (safeLocalStorageGet('dashboard-uni-filter-value') || uni) ?? 'STG';
      } else if (groups && groups.includes('ImmigrationProvider')) {
        role = UserRole.ImmigrationProvider;
        newUni = (safeLocalStorageGet('dashboard-uni-filter-value') || uni) ?? 'STG';
      }

      return {
        ...state,
        isLoggedIn: true,
        isAuthenticating: false,
        student_number: preferredUsername ?? username,
        email,
        role,
        groups: groups || [],
        terms,
        uni: newUni,
        intake,
        institution,
        agencyId: agency,
        isPathway: !!pathway,
        pathwayAccess: pathwayAccess ?? 'None',
        partner,
        serviceProviderType: getServiceProviderType(partner),
        validation_errors: undefined,
        server_errors: undefined,
      };
    }
    case AuthActionTypes.ACCEPT_TERMS_SUCCESS:
      return {
        ...state,
        terms: true,
      };
    case AuthActionTypes.SET_USERS_UNI: {
      return {
        ...state,
        uni: action.payload || '',
        pathway: undefined,
        intake: '', // Clear intake when university changes
      };
    }

    case AuthActionTypes.SET_USERS_INTAKE: {
      return {
        ...state,
        intake: action.payload || '',
      };
    }

    case AuthActionTypes.TESTING: {
      const [, params]: [any, string] = action.payload?.split('?') || [];
      const parsedParams = new URLSearchParams(params);
      const role = parsedParams.get('userRole');
      const terms = parsedParams.get('terms');

      return {
        ...state,
        isTesting: true,
        isLoggedIn: true,
        isAuthenticating: false,
        uni: 'STG',
        role: role ? (role as UserRole) : UserRole.Uni,
        terms: role === '[Agent]' ? terms === 'true' : true,
        student_number: '1111111',
        email: 'test@enroly.com',
        agencyId: 'TestAgency',
        validation_errors: undefined,
        server_errors: undefined,
      };
    }

    case AuthActionTypes.GET_CURRENT_USER_SUCCESS: {
      return {
        ...state,
        currentUser: {
          ...state.currentUser,
          ...action.payload,
        },
      };
    }
    case AuthActionTypes.GET_AUTH_AGENCY_REQUEST: {
      return {
        ...state,
        authAgencyLoading: true,
        authAgencyError: null,
      };
    }
    case AuthActionTypes.GET_AUTH_AGENCY_SUCCESS: {
      return {
        ...state,
        authAgencyData: action.payload,
        authAgencyLoading: false,
      };
    }
    case AuthActionTypes.GET_AUTH_AGENCY_FAILURE: {
      return {
        ...state,
        authAgencyLoading: false,
        authAgencyError: action.payload,
      };
    }
    case 'REDUX_WEBSOCKET::OPEN': {
      return {
        ...state,
        wsConnected: true,
      };
    }
    case 'REDUX_WEBSOCKET::BROKEN':
    case 'REDUX_WEBSOCKET::CLOSED':
    case 'REDUX_WEBSOCKET::WEBSOCKET_DISCONNECT': {
      return {
        ...state,
        wsConnected: false,
      };
    }
    default:
      return state;
  }
};

export { reducer as authReducer };
